import { useMemo, useState, type ReactElement } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ListConfigsFilters } from '@/api/client';
import { getErrorCodeForUi } from '@/api/errors';
import { can } from '@/auth/permissions';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import {
  useAdminConfigMutations,
  useConfigCatalog,
  useConfigList,
  useReencryptSecrets,
  type ConfigItemViewModel,
} from '@/features/admin-configs/api';
import { AdminConfigsTable } from '@/features/admin-configs/components/AdminConfigsTable';
import { ConfigDetailSheet } from '@/features/admin-configs/components/ConfigDetailSheet';
import {
  ConfigToolbar,
  type ConfigToolbarOptions,
} from '@/features/admin-configs/components/ConfigToolbar';

const EMPTY_FILTERS: ListConfigsFilters = {};

function uniqueSorted(values: readonly string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

export function AdminConfigsPage(): ReactElement {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListConfigsFilters>(EMPTY_FILTERS);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const auth = useAuth();
  const canManage = can(auth.user, 'manageConfig');

  const catalogQuery = useConfigCatalog();
  const listQuery = useConfigList(filters);
  const { upsert, reset } = useAdminConfigMutations();
  const reencrypt = useReencryptSecrets();

  const toolbarOptions: ConfigToolbarOptions = useMemo(() => {
    const defs = catalogQuery.data ?? [];
    return {
      namespaces: uniqueSorted(defs.map((d) => d.namespace)),
      tags: uniqueSorted(defs.flatMap((d) => d.tags)),
      scopes: uniqueSorted(defs.map((d) => d.scope)),
    };
  }, [catalogQuery.data]);

  const configs = useMemo(
    () =>
      listQuery.isSuccess
        ? [...listQuery.data].sort((a, b) => a.key.localeCompare(b.key))
        : [],
    [listQuery.isSuccess, listQuery.data],
  );

  // Track the live row from the freshly refetched list so the drawer reflects
  // post-save source/value changes; fall back to null once it disappears.
  const selected: ConfigItemViewModel | null = useMemo(
    () => configs.find((c) => c.key === selectedKey) ?? null,
    [configs, selectedKey],
  );

  const selectedEnumValues = useMemo(() => {
    if (selectedKey === null) return null;
    return catalogQuery.data?.find((d) => d.key === selectedKey)?.enumValues ?? null;
  }, [catalogQuery.data, selectedKey]);

  function handleFilterChange(next: Partial<ListConfigsFilters>): void {
    setFilters((prev) => ({ ...prev, ...next }));
  }

  function handleCloseSheet(): void {
    if (upsert.isPending || reset.isPending) return;
    setSelectedKey(null);
    upsert.reset();
    reset.reset();
  }

  function handleSave(input: { value: string; reason?: string }): void {
    if (selectedKey === null) return;
    upsert.mutate(
      { key: selectedKey, value: input.value, ...(input.reason !== undefined ? { reason: input.reason } : {}) },
      {
        onSuccess: () => {
          upsert.reset();
        },
      },
    );
  }

  function handleReset(): void {
    if (selectedKey === null) return;
    reset.mutate(selectedKey, {
      onSuccess: () => {
        reset.reset();
      },
    });
  }

  return (
    <>
      <PageHeader
        icon={<SlidersHorizontal className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.configs.heading')}
        subtitle={t('routes.admin.configs.subheading')}
      />
      <div className="space-y-4 bg-background px-8 py-5">
        {canManage && (
          <div className="flex items-center justify-end gap-3">
            {reencrypt.isSuccess && (
              <span className="text-xs text-muted-foreground" role="status">
                {t('routes.admin.configs.reencrypt.success', {
                  count: reencrypt.data.reencrypted,
                })}
              </span>
            )}
            {reencrypt.isError && (
              <span className="text-xs text-red-600" role="alert">
                {t('routes.admin.configs.reencrypt.error', {
                  code: getErrorCodeForUi(reencrypt.error),
                })}
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={reencrypt.isPending}
              onClick={() => {
                reencrypt.mutate();
              }}
            >
              {t('routes.admin.configs.reencrypt.button')}
            </Button>
          </div>
        )}

        <ConfigToolbar filters={filters} options={toolbarOptions} onChange={handleFilterChange} />

        {listQuery.isPending && <ConfigsListSkeleton />}

        {listQuery.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={listQuery.error}
              heading={t('routes.admin.configs.error.heading')}
              message={t('routes.admin.configs.error.message')}
              codeLabel={t('routes.admin.configs.error.codeLabel')}
              onRetry={() => {
                listQuery.refetch().catch(() => undefined);
              }}
              retryLabel={t('routes.admin.configs.error.retry')}
            />
          </div>
        )}

        {listQuery.isSuccess && configs.length === 0 && (
          <p className="text-sm text-muted-foreground" role="status">
            {t('routes.admin.configs.empty')}
          </p>
        )}

        {listQuery.isSuccess && configs.length > 0 && (
          <AdminConfigsTable
            configs={configs}
            selectedKey={selectedKey}
            onSelect={(config) => {
              upsert.reset();
              reset.reset();
              setSelectedKey(config.key);
            }}
          />
        )}
      </div>

      <ConfigDetailSheet
        config={selected}
        enumValues={selectedEnumValues}
        canEditSensitive={canManage}
        onClose={handleCloseSheet}
        onSave={handleSave}
        onReset={handleReset}
        savePending={upsert.isPending}
        resetPending={reset.isPending}
        saveError={upsert.error}
        resetError={reset.error}
      />
    </>
  );
}

function ConfigsListSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
